import { useCallback, useMemo } from "react";
import { useDocumentCurrency } from "@/ProposalBuilder/contexts/DocumentCurrencyContext";
import { useCurrencyConfig } from "@/Hooks/useCurrencyConfig";
import { currencyFormatter, getCurrencySettings } from "@/Utils/Currency";

export interface UseDocumentDisplayCurrencyResult {
	/** Code the document is authored in (== quote currency). Equal to `companyCurrency` for legacy docs. */
	quoteCurrency: string;
	/** Code of the company's primary currency. */
	companyCurrency: string;
	/** True when the document's currency was frozen at status freeze. */
	isFrozen: boolean;
	/** True when the document carries no secondary currency context (legacy doc / opportunity in primary currency). */
	isLegacy: boolean;
	/** True when the user has flipped the toggle to show quote-currency values. */
	isQuoteView: boolean;
	/** The currency code currently being displayed (quote or company). */
	displayCurrency: string;
	/** Currency code the conversion tooltip should reveal (the opposite of displayCurrency). */
	oppositeCurrency: string;
	/**
	 * Exchange rate from quote → company. `1 {quoteCurrency} = effectiveRate {companyCurrency}`.
	 * For frozen docs this is the rate captured at signature; otherwise the live admin-set rate.
	 * Returns 1 for legacy docs (no conversion).
	 */
	effectiveRate: number;
	/** Returns the supplied quote-currency amount expressed in `displayCurrency`. */
	effectiveAmount: (quoteAmount: number) => number;
	/** Returns the supplied quote-currency amount expressed in `oppositeCurrency` — i.e. for the conversion tooltip. */
	oppositeAmount: (quoteAmount: number) => number;
	formatAmount: (
		quoteAmount: number | string | null | undefined,
		fallbackValue?: string,
		currencyOptions?: Intl.NumberFormatOptions
	) => string;
	/** Flip the toggle. */
	toggle: () => void;
	/** Set the toggle explicitly. */
	setQuoteView: (next: boolean) => void;
}

export const useDocumentDisplayCurrency =
	(): UseDocumentDisplayCurrencyResult => {
		const docCtx = useDocumentCurrency();
		const skipLiveConfigFetch =
			(docCtx?.skipLiveConfigFetch ?? false) ||
			((docCtx?.isFrozen ?? false) && !!docCtx?.companyCurrency);
		const { config } = useCurrencyConfig({ skip: skipLiveConfigFetch });

		const isQuoteView = docCtx?.isQuoteView ?? false;
		const setQuoteView = docCtx?.setQuoteView ?? (() => {});

		const companyCurrency =
			docCtx?.companyCurrency ??
			config?.primaryCurrency.code ??
			getCurrencySettings().currency;
		const quoteCurrency = docCtx?.currency ?? companyCurrency;
		const isLegacy = !docCtx || docCtx.currency === null;
		const isFrozen = docCtx?.isFrozen ?? false;

		const liveRate = useMemo(() => {
			if (isLegacy) return 1;
			const secondary = config?.currencyConfigs.find(
				c => c.currency.code === quoteCurrency
			);
			return secondary?.exchangeRate ?? 1;
		}, [config, quoteCurrency, isLegacy]);

		const effectiveRate = isFrozen
			? docCtx?.frozenRate ?? liveRate
			: liveRate;

		const displayCurrency = isQuoteView ? quoteCurrency : companyCurrency;
		const oppositeCurrency = isQuoteView ? companyCurrency : quoteCurrency;

		const effectiveAmount = useCallback(
			(quoteAmount: number) =>
				isQuoteView ? quoteAmount : quoteAmount * effectiveRate,
			[isQuoteView, effectiveRate]
		);

		const oppositeAmount = useCallback(
			(quoteAmount: number) =>
				isQuoteView ? quoteAmount * effectiveRate : quoteAmount,
			[isQuoteView, effectiveRate]
		);

		const formatAmount = useCallback(
			(
				quoteAmount: number | string | null | undefined,
				fallbackValue = "",
				currencyOptions?: Intl.NumberFormatOptions
			) =>
				currencyFormatter(quoteAmount, fallbackValue, currencyOptions, {
					currency: quoteCurrency
				}),
			[quoteCurrency]
		);

		const toggle = useCallback(
			() => setQuoteView(!isQuoteView),
			[isQuoteView, setQuoteView]
		);

		return {
			quoteCurrency,
			companyCurrency,
			isFrozen,
			isLegacy,
			isQuoteView,
			displayCurrency,
			oppositeCurrency,
			effectiveRate,
			effectiveAmount,
			oppositeAmount,
			formatAmount,
			toggle,
			setQuoteView
		};
	};
